import { useHandleResponse } from '@/utils/useHandleResponse';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, AutoComplete, Button, Form, Select, Spin, Tabs } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { setCompanies } from '../../../contextRedux/Company/CompanyStore';
import { resetDevice } from '../../../contextRedux/DeviceType/DeviceTypeModel';
import { setDevices } from '../../../contextRedux/DeviceType/DeviceTypeStore';
import { setSystemTypes } from '../../../contextRedux/SystemType/SystemTypeStore';
import { setUsers } from '../../../contextRedux/User/UserStore';
import { addWorkPlaceItems, setWorkPlace, updateField } from '../../../contextRedux/WorkPlace/WorkPlaceModel';
import CompanyService from '../../../services/company/CompanyService';
import DeviceTypeService from '../../../services/deviceType/DeviceTypeService';
import SystemTypeService from '../../../services/systemType/SystemTypeService';
import UserService from '../../../services/user/UserService';
import WorkPlaceService from '../../../services/workPlace/WorkPlaceService';
import { RootState } from '../../../store';
import { Tab } from '../../../utils/tabs';
import AgentTab from './Tabs/AgentTab/AgentTab';
import ContactPersonTab from './Tabs/ContactPersonTab/ContactPersonTab';
import DeviceTab from './Tabs/DeviceTab/DeviceTab';
import LocationTab from './Tabs/LocationTab/LocationTab';
import TimeTableTab from './Tabs/TimeTableTab/TimeTableTab';

const NewWorkPlace: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { Option } = Select;
  const handleResponse = useHandleResponse();

  const location = useLocation();

  const useQueryParams = () => {
    return new URLSearchParams(useLocation().search);
  };

  const query = useQueryParams();
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [id, setId] = useState<string | null>(query.get('id'));

  const [companyId, setCompanyId] = useState<string | null>(query.get('companyId'));
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedTabKey, setSelectedTabKey] = useState<string>('locationTab');

  const deviceType = useSelector((state: RootState) => state?.models?.deviceType);
  const reduxWorkPlace = useSelector((state: RootState) => state?.models?.workPlace);
  const initialValues = reduxWorkPlace || {};

  useEffect(() => {
    const idFromUrl = query.get('id');
    const companyIdFromUrl = query.get('companyId');
    if (!!companyIdFromUrl) {
      setCompanyId(companyIdFromUrl);
    }
    setId(idFromUrl);
  }, [location.search]);

  useEffect(() => {
    if (id) {
      dispatch(updateField({ field: 'id', value: id }));
    } else {
      dispatch(updateField({ field: 'id', value: null }));
    }
  }, [id, dispatch]);

  const { data: _workPlace, isFetching: isWorkPlaceLoading } = useQuery({
    queryKey: ['workPlaces'],
    queryFn: () =>
      WorkPlaceService.search({
        ids: [id!],
        isAgentIncluded: true,
        isUserIncluded: true,
        isWorkPlaceItemIncluded: true,
        isCompanyIncluded: true,
      }).then((data) => {
        dispatch(setWorkPlace(data?.response[0]));
        return data?.response[0];
      }),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _agents, isFetching: isAgentsLoading } = useQuery({
    queryKey: ['agents'],
    queryFn: () =>
      UserService.search({
        isActive: true,
      }).then((data) => {
        dispatch(setUsers(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _systemTypes, isFetching: isSystemTypesLoading } = useQuery({
    queryKey: ['systems'],
    queryFn: () =>
      SystemTypeService.search({
        isActive: true,
      }).then((data) => {
        dispatch(setSystemTypes(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _deviceTypes, isFetching: isDeviceTypesLoading } = useQuery({
    queryKey: ['devices', deviceType?.props?.isFinish],
    queryFn: () =>
      DeviceTypeService.search({
        isSystemTypeIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setDevices(data?.response));
        const findNewDeviceType = data?.response?.find((d: any) => d.name === deviceType?.name);
        if (findNewDeviceType) {
          const newWorkPlaceItem = {
            id: '',
            workPlaceId: reduxWorkPlace?.id,
            systemTypeId: findNewDeviceType?.systemTypeId,
            systemType: findNewDeviceType?.systemType,
            deviceTypeId: findNewDeviceType?.id,
            deviceType: findNewDeviceType,
            piece: 1,
            isAnotherProvider: false,
          };
          dispatch(addWorkPlaceItems(newWorkPlaceItem));
          dispatch(resetDevice({}));
        }

        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: companies, isFetching: isCompaniesLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        ids: !!companyId ? [companyId] : [],
        isActive: true,
      }).then((data) => {
        dispatch(setCompanies(data?.response));
        return data?.response;
      }),
    enabled: !!companyId || id == null || id == '',
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const handleFilter = (inputValue: string, option: any) => {
    return option.value.toLowerCase().includes(inputValue.toLowerCase());
  };

  const handleSelectClient = (value: string) => {
    const company = companies?.find((company: any) => company?.name === value);
    if (company) {
      const fieldsValue = {
        ...form?.getFieldsValue(true),
        companyId: company?.id,
        company: { name: company?.name, id: company?.id },
      };
      form.setFieldsValue(fieldsValue);
      dispatch(updateField({ field: 'companyId', value: company?.id }));
      dispatch(updateField({ field: 'company', value: company }));
    }
  };

  const handleFieldChange = (changedValues: any, _allValues: any) => {
    for (const key in changedValues) {
      dispatch(updateField({ field: key, value: changedValues[key] }));
    }
  };

  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveWorkPlace = async () => {
    try {
      await onSaveWorkPlace();
    } catch (errorInfo: any) {
      message.error(errorInfo?.errorFields[0]?.errors);
    }
  };

  const onSaveWorkPlace = async () => {
    saveWorkPlace(reduxWorkPlace);
  };

  const { mutate: saveWorkPlace, isPending: _isSavingWorkPlace } = useMutation({
    mutationFn: (formData: any) => WorkPlaceService.upsert(id, formData),
    onSuccess: (data: any) => {
      if (data) {
        if (data?.errors?.length > 0) {
          data?.errors.forEach((error: any) => {
            message.error(error);
          });
          return;
        }
        handleResponse(data, data?.response?.name);
        data?.response && navigate(`/administration/workPlace/edit?id=${data?.response?.id}`);
      }
    },
    onError: (error: any) => {
      message.error(error.message);
    },
  });

  const tabs: Tab[] = [
    {
      key: 'locationTab',
      label: 'Locatia',
      children: <LocationTab />,
    },
    {
      key: 'timeTableTab',
      label: 'Orar',
      children: <TimeTableTab />,
    },
    {
      key: 'contactPersonTab',
      label: 'Persoana De Contact',
      children: <ContactPersonTab />,
    },
    {
      key: 'agentTab',
      label: 'Agenti',
      children: <AgentTab />,
    },
    {
      key: 'deviceTab',
      label: 'Device-uri',
      children: <DeviceTab />,
    },
  ];

  if (isWorkPlaceLoading || isCompaniesLoading || isAgentsLoading || isSystemTypesLoading || isDeviceTypesLoading) {
    return <Spin size='large' className='spinner'></Spin>;
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <h2>{id ? 'Punct De Lucru - ' + reduxWorkPlace?.name : 'Punct De Lucru Nou'}</h2>
        <div>
          <Button onClick={() => handleSaveWorkPlace()}>Salveaza</Button>
        </div>
      </div>
      <Form
        form={form}
        name='workPlaceForm'
        initialValues={initialValues}
        onValuesChange={handleFieldChange}
        layout='vertical'
        style={{ margin: '20px 20px' }}
      >
        <Form.Item name={['company', 'name']} rules={[{ required: true, message: 'Introdu client!' }]}>
          <AutoComplete
            onSelect={handleSelectClient}
            onChange={(value: string) => handleSearchTermChange(value)}
            filterOption={handleFilter}
            value={searchTerm}
            style={{ width: '100%' }}
          >
            {companies?.map((company: any, index: any) => (
              <Option key={index} value={company?.name}>
                {company?.name}
              </Option>
            ))}
          </AutoComplete>
        </Form.Item>
      </Form>
      <Tabs
        activeKey={selectedTabKey}
        onChange={setSelectedTabKey} // Update selected tab on change
        items={tabs}
      />
    </>
  );
};

export default NewWorkPlace;
